/**
 * FRONT-3368: Undo on a publish's success toast. It reverses the publish the
 * toast announced, by the id the publish answered with, so a later publish
 * is never the one undone. The server names a publish only for the owner of
 * an app that keeps Git history, so no answer means no Undo.
 */
import { toast } from 'sonner';
import type { QueryClient } from '@tanstack/react-query';
import { appActivityKeys, appDraftKeys } from '~/lib/query-keys';
import { undoPublishRequest } from '~/hooks/queries/use-app-activity';
import { undoneMessage } from './activity-tab';

export const UNDO_LABEL = 'Undo';
export const UNDO_WINDOW_MS = 10_000;

/** Undoes `publishId` and says what happened; true when it was undone. */
export async function undoAnnouncedPublish(qc: QueryClient, slug: string, publishId: number): Promise<boolean> {
  let undone = false;
  try {
    const answer = await undoPublishRequest(slug, publishId);
    toast.success(undoneMessage({ kind: 'publish', change: answer.change, wentOffline: answer.wentOffline }));
    undone = true;
  } catch (e) {
    toast.error(e instanceof Error && e.message ? e.message : 'Could not undo the publish');
  }
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['admin-app', slug] }),
    qc.invalidateQueries({ queryKey: ['admin-apps'] }),
    qc.invalidateQueries({ queryKey: ['app-published-card'] }),
    qc.invalidateQueries({ queryKey: appActivityKeys.list(slug) }),
    qc.invalidateQueries({ queryKey: appDraftKeys.list(slug) }),
  ]);
  return undone;
}

/**
 * The success toast's options offering Undo for the publish `answer` names;
 * undefined when it names none. `cancel` puts Undo in sonner's second slot,
 * for a toast whose action is already taken (Open app).
 */
export function publishUndoToast(qc: QueryClient, slug: string, answer: { publishId?: number | null }, slot: 'action' | 'cancel' = 'action') {
  const publishId = answer.publishId;
  if (typeof publishId !== 'number') return undefined;
  const undo = { label: UNDO_LABEL, onClick: () => void undoAnnouncedPublish(qc, slug, publishId) };
  return slot === 'action' ? { action: undo, duration: UNDO_WINDOW_MS } : { cancel: undo, duration: UNDO_WINDOW_MS };
}
